/*
  Home page, phones only: a row of dots under every swiping row (featured
  collections, category tiles, video row), the same as "Most people add" in
  the cart drawer. Rows are found by how they behave - a horizontal
  scroll-snap track wider than the screen - so swipe sections added later get
  dots without any changes here.

  Rows get one dot per stop, up to 12. Looping rows (the category tiles and
  the video row repeat their items 3-5 times so they can scroll endlessly,
  and start in a middle copy) get one dot per real item, and the dot follows
  the item you are on in whichever copy - counting every copy as its own stop
  made the dot lag several swipes behind. Longer rows move the dot in
  proportion. Dots are buttons: tapping one scrolls there. Loaded from layout/theme.liquid on the index template and the
  fit-out builder page (page.kit-builder), whose product rows render after
  categories are picked - the MutationObserver below catches those.

  Cost: the first scan waits for the page load and an idle moment (the first
  dot row sits below the fold), every pass reads all the layout it needs
  before writing anything (interleaving the two forced a fresh layout per row),
  resizes that only change the height (the phone address bar collapsing while
  scrolling) are ignored, and the page is only watched for new rows where rows
  arrive late (the fit-out builder, data-watch on the script tag).
*/
(function () {
  var WATCH = !!(document.currentScript && document.currentScript.hasAttribute('data-watch'));
  var MQ = window.matchMedia('(max-width: 749px)');
  var MAX_DOTS = 12;
  var CANDIDATES = 'main ul, main [class*="slider"], main [class*="track"], main [class*="carousel"]';
  var rows = [];

  var style = document.createElement('style');
  style.textContent =
    '.swipe-dots{display:none}' +
    '@media screen and (max-width:749px){.swipe-dots{display:flex;justify-content:center;gap:.6rem;margin:.6rem 0 1.6rem}.swipe-dots[hidden]{display:none}}' +
    '.swipe-dots button{position:relative;width:.6rem;height:.6rem;padding:0;border:0;border-radius:999px;background:#d6d6d6;cursor:pointer;transition:width .2s ease,background-color .2s ease}' +
    '.swipe-dots button::after{content:"";position:absolute;inset:-.8rem -.4rem}' +
    '.swipe-dots button[aria-current]{width:1.6rem;background:#121212}';
  document.head.appendChild(style);

  // The cards, either directly in the scrolling element or in its single child
  // list (the fit-out builder scrolls a viewport wrapped around a <ul>).
  function itemsOf(el) {
    if (el.children.length === 1 && el.firstElementChild.children.length > 1) {
      return el.firstElementChild.children;
    }
    return el.children;
  }

  function isTrack(el) {
    if (el.closest('cart-drawer, .swipe-dots')) return false;
    var cs = getComputedStyle(el);
    if (cs.overflowX !== 'auto' && cs.overflowX !== 'scroll') return false;
    if (cs.scrollSnapType.indexOf('x') === -1) return false;
    return itemsOf(el).length > 1 && el.scrollWidth > el.clientWidth + 10;
  }

  // Scroll positions the row can snap to, one per visible item, capped at the end.
  function stopsFor(track) {
    var max = track.scrollWidth - track.clientWidth;
    var base = track.getBoundingClientRect().left - track.scrollLeft;
    var pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    var seen = {};
    var stops = [];
    Array.prototype.forEach.call(itemsOf(track), function (child) {
      var r = child.getBoundingClientRect();
      if (!r.width) return;
      var x = Math.max(0, Math.min(Math.round(r.left - base - pad), max));
      if (seen[x]) return;
      seen[x] = true;
      stops.push(x);
    });
    return stops.sort(function (a, b) { return a - b; });
  }

  // What identifies an item, so repeated copies of the same item match.
  function signature(el) {
    var link = el.querySelector('a[href]');
    if (link) return link.getAttribute('href');
    // Video files before images: cloned copies' images can still be lazy.
    var video = el.querySelector('video source[src], video[src]');
    if (video) return video.getAttribute('src');
    var img = el.querySelector('img');
    if (img) return img.getAttribute('data-src') || img.getAttribute('src') || '';
    return (el.textContent || '').trim().slice(0, 60);
  }

  // Length of the repeating run when the row is the same items repeated, else 0.
  function loopPeriod(items) {
    var n = items.length;
    // Sliders that loop mark their copies (the video row: "ss-clone"); the
    // originals are one full run. Its videos only load near the screen, so
    // the copies can't be matched by content.
    var originals = items.filter(function (c) { return !/(^|[\s_-])clone\b/.test(c.className); }).length;
    if (originals && originals < n && n % originals === 0) return originals;
    var sigs = items.map(signature);
    // Items with nothing to tell them apart are not a loop.
    if (sigs.every(function (sig) { return sig === sigs[0]; })) return 0;
    for (var p = 2; p <= n / 2; p++) {
      if (n % p) continue;
      var same = true;
      for (var i = p; i < n; i++) {
        if (sigs[i] !== sigs[i % p]) { same = false; break; }
      }
      if (same) return p;
    }
    return 0;
  }

  // Scroll position that brings each item to its snap point (no
  // de-duplication), for looping rows. From layout offsets, not screen
  // rects: the video row scales and shifts its slides with transforms. Items
  // snapping to the centre (the video row) are centred; the rest start at
  // the scroll padding.
  function positionsFor(track, items) {
    var pad = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    var origin = items[0].offsetParent === track ? 0 : track.offsetLeft;
    return items.map(function (child) {
      var x = child.offsetLeft - origin;
      var align = getComputedStyle(child).scrollSnapAlign;
      if (align.indexOf('center') !== -1) return Math.round(x + child.offsetWidth / 2 - track.clientWidth / 2);
      if (align.indexOf('end') !== -1) return Math.round(x + child.offsetWidth - track.clientWidth);
      return Math.round(x - pad);
    });
  }

  function nearest(list, left) {
    var best = 0;
    list.forEach(function (x, i) {
      if (Math.abs(x - left) < Math.abs(list[best] - left)) best = i;
    });
    return best;
  }

  // Reads only: works out the row's stops and dot count.
  function measure(row) {
    // Sit 6px under the cards even when the row keeps a bottom margin (the
    // featured collection rows have 15px), so the dots read as part of the
    // row rather than floating towards the "Shop all" button below.
    row.marginTop = (6 - (parseFloat(getComputedStyle(row.track).marginBottom) || 0)) + 'px';
    var items = Array.prototype.filter.call(itemsOf(row.track), function (c) {
      return c.getBoundingClientRect().width > 0;
    });
    row.loop = loopPeriod(items);
    var count;
    if (row.loop) {
      row.positions = positionsFor(row.track, items);
      row.proportional = row.loop > MAX_DOTS;
      count = Math.min(row.loop, MAX_DOTS);
    } else {
      var stops = stopsFor(row.track);
      row.stops = stops;
      row.proportional = stops.length > MAX_DOTS;
      count = row.proportional ? MAX_DOTS : stops.length;
    }
    row.count = count;
    row.active = activeIndex(row);
  }

  // Writes only, from what measure() found.
  function paint(row) {
    var count = row.count;
    row.dots.style.marginTop = row.marginTop;
    row.dots.hidden = count < 2;
    if (row.dots.childElementCount !== count) {
      var html = '';
      for (var i = 0; i < count; i++) {
        html += '<button type="button" data-index="' + i + '" aria-label="Show part ' + (i + 1) + ' of ' + count + '"></button>';
      }
      row.dots.innerHTML = html;
    }
    setActive(row, row.active);
  }

  function activeIndex(row) {
    var left = row.track.scrollLeft;
    if (row.loop) {
      var item = nearest(row.positions, left) % row.loop;
      return row.proportional ? Math.round((item / (row.loop - 1)) * (MAX_DOTS - 1)) : item;
    }
    if (row.proportional) {
      var max = row.track.scrollWidth - row.track.clientWidth || 1;
      return Math.round((left / max) * (MAX_DOTS - 1));
    }
    var best = 0;
    row.stops.forEach(function (x, i) {
      if (Math.abs(x - left) < Math.abs(row.stops[best] - left)) best = i;
    });
    return best;
  }

  function mark(row) {
    setActive(row, activeIndex(row));
  }

  function setActive(row, active) {
    Array.prototype.forEach.call(row.dots.children, function (dot, i) {
      dot.toggleAttribute('aria-current', i === active);
    });
  }

  function attach(track) {
    var dots = document.createElement('div');
    dots.className = 'swipe-dots';
    track.after(dots);
    var row = { track: track, dots: dots, stops: [], proportional: false, frame: null };
    rows.push(row);

    track.addEventListener('scroll', function () {
      if (row.frame) return;
      row.frame = requestAnimationFrame(function () {
        row.frame = null;
        mark(row);
      });
    }, { passive: true });

    dots.addEventListener('click', function (e) {
      var dot = e.target.closest('button[data-index]');
      if (!dot) return;
      var i = Number(dot.dataset.index);
      var x;
      if (row.loop) {
        // The copy of that item closest to where the row is now.
        var target = row.proportional ? Math.round((i * (row.loop - 1)) / (MAX_DOTS - 1)) : i;
        var left = track.scrollLeft;
        x = null;
        row.positions.forEach(function (pos, k) {
          if (k % row.loop !== target) return;
          if (x === null || Math.abs(pos - left) < Math.abs(x - left)) x = pos;
        });
      } else if (row.proportional) {
        var max = track.scrollWidth - track.clientWidth;
        x = (max * i) / (MAX_DOTS - 1);
      } else {
        x = row.stops[i];
      }
      track.scrollTo({ left: x, behavior: 'smooth' });
    });
  }

  function known(el) {
    for (var i = 0; i < rows.length; i++) if (rows[i].track === el) return true;
    return false;
  }

  function scan() {
    if (!MQ.matches) return;
    // Read: find new rows, then measure every row. Write: add and paint dots.
    var found = Array.prototype.filter.call(document.querySelectorAll(CANDIDATES), function (el) {
      return !known(el) && isTrack(el);
    });
    var before = rows.length;
    found.forEach(attach);
    var fresh = rows.slice(before);
    // Adding the new dot rows costs one layout here, not one per row.
    rows.forEach(measure);
    rows.forEach(paint);
    return fresh;
  }

  var scanTimer;
  function scanSoon() {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(scan, 200);
  }

  var lastWidth = window.innerWidth;
  function onResize() {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    scanSoon();
  }

  function start() {
    scan();
    window.addEventListener('resize', onResize);
    if (MQ.addEventListener) MQ.addEventListener('change', scanSoon);
    var main = document.querySelector('main');
    if (WATCH && main && window.MutationObserver) {
      new MutationObserver(scanSoon).observe(main, { childList: true, subtree: true });
    }
  }

  function whenIdle() {
    if (window.requestIdleCallback) requestIdleCallback(start, { timeout: 2000 });
    else setTimeout(start, 200);
  }

  if (document.readyState === 'complete') whenIdle();
  else window.addEventListener('load', whenIdle);
})();
